import React, { useState } from 'react'
import { Modal, Row, Col, Button } from 'react-bootstrap'
import styled from 'styled-components'
import { Model } from '../../types/models/model'
import { useAppSelector } from '../../store/hooks'
import {
  selectSelectedTrainingSource,
  selectSelectedValidationSource,
} from '../../store/slices/datasetSlice'
import { useGetProjectQuery } from '../../services/projectsApi'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import SelectedTrainingSourceCard from '../SelectedTrainingSourceCard'
import SelectedValidationSourceCard from '../SelectedValidationSourceCard'
import { SelectedTagsCard } from '../views/ModelGenerationView/components/SelectedTagsCard'
import ModelItem from '../styled/ModelSelect/ModelItem'
import { PerformanceCard } from '../views/ModelsOverview/components'
import { GradientButton } from '../styled/GradientButton'

const StyledModal = styled(Modal)`
  .modal-content {
    background: #2a2a2a;
    border: none;
    border-radius: 12px;
    color: white;
  }

  .modal-header {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: 1.5rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .modal-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding: 1.5rem;
  }
`

interface FineTuneModalProps {
  show: boolean
  onHide: () => void
  model: Model | undefined
  onFineTune: () => void
  isFineTuning: boolean
}

const FineTuneModal: React.FC<FineTuneModalProps> = ({
  show,
  onHide,
  model,
  onFineTune,
  isFineTuning,
}) => {
  const selectedTrainingSources = useAppSelector(selectSelectedTrainingSource)
  const selectedValidationSources = useAppSelector(selectSelectedValidationSource)
  const projectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [selectedTrainingSourcesLocal, setSelectedTrainingSourcesLocal] = useState<string[]>(
    selectedTrainingSources || []
  )
  const [selectedValidationSourcesLocal, setSelectedValidationSourcesLocal] = useState<string[]>(
    selectedValidationSources || []
  )

  return (
    <StyledModal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Fine-Tune</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            {model && (
              <>
                <div style={{ marginTop: '1rem' }}>
                  <ModelItem model={model} project={project} $minimized={true} />
                </div>
                <div className="mt-3">
                  <PerformanceCard model={model} small />
                </div>
              </>
            )}
          </Col>
          <Col md={6} className="h-100">
            <div className="mb-3">
              <SelectedTrainingSourceCard
                selectedSources={selectedTrainingSourcesLocal}
                onSourcesChange={setSelectedTrainingSourcesLocal}
                readOnly={isFineTuning}
              />
            </div>
            <div>
              <SelectedValidationSourceCard
                selectedSources={selectedValidationSourcesLocal}
                onSourcesChange={setSelectedValidationSourcesLocal}
                readOnly={isFineTuning}
              />
            </div>
            <SelectedTagsCard
              selectedTagIds={selectedTagIds}
              onTagSelectionChange={setSelectedTagIds}
              disabled={isFineTuning}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isFineTuning}>
          Cancel
        </Button>
        <GradientButton
          onClick={onFineTune}
          disabled={
            isFineTuning ||
            !selectedTrainingSourcesLocal.length ||
            !selectedValidationSourcesLocal.length ||
            !model?.id
          }
        >
          {isFineTuning ? 'Fine-Tuning...' : 'Start Fine-Tuning'}
        </GradientButton>
      </Modal.Footer>
    </StyledModal>
  )
}

export default FineTuneModal
